import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Grid, 
  Avatar, 
  IconButton, 
  Tooltip 
} from "@mui/material";
import { 
  TrendingUp, 
  Inventory, 
  AttachMoney, 
  CalendarToday, 
  FilterList,
  Analytics,
  Assessment
} from "@mui/icons-material";
import Loader from "../../../components/layout/Loader";
import { 
  fetchCostAndInventoryUtilization, 
  resetCostAndInventoryUtilization 
} from "../../../redux/actions/CostSummalryActions";
import AnalyticsTabs from "./AnalyticsTabs";

const CostAndInventoryUtilizationAnalytics = ({ nav }) => {
  const dispatch = useDispatch();
  const { loading, error, data = { costSummary: [], inventoryUtilization: [] } } = useSelector(
    (state) => state.costAndInventoryUtilization || {}
  );
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [productType, setProductType] = useState("");
  const [value, setValue] = useState(0);

  useEffect(() => {
    dispatch(fetchCostAndInventoryUtilization(startDate, endDate, productType, userInfo.token));
    return () => dispatch(resetCostAndInventoryUtilization());
  }, [dispatch, startDate, endDate, productType]);

  const handleTabChange = (event, newValue) => setValue(newValue);

  const totalCost = data.costSummary.reduce((sum, item) => sum + (item?.totalCost || 0), 0);
  const totalOrders = data.costSummary.reduce((sum, item) => sum + (item?.totalOrders || 0), 0);
  const avgOrderValue = totalOrders > 0 ? totalCost / totalOrders : 0;
  const totalItems = data.inventoryUtilization.length;

  if (loading) return <Loader />;
  if (error) return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ bgcolor: 'rgba(220, 38, 38, 0.1)', border: '1px solid #dc2626' }}>
        <CardContent>
          <Typography color="#ffffff" variant="h6">Error: {error}</Typography>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <div className={nav.isSidebar} style={{ backgroundColor: 'transparent', minHeight: '100vh' }}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: '#ffffff', mb: 1, fontWeight: 'bold' }}>
            <Analytics sx={{ mr: 2, verticalAlign: 'middle' }} />
            Cost & Inventory Analytics
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Comprehensive analysis of cost efficiency and inventory utilization
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgba(59, 130, 246, 0.1)', 
              border: '1px solid rgba(59, 130, 246, 0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#3b82f6', mr: 2 }}>
                    <AttachMoney />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>Total Cost</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                  ${totalCost.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgba(16, 185, 129, 0.1)', 
              border: '1px solid rgba(16, 185, 129, 0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#10b981', mr: 2 }}>
                    <TrendingUp />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>Total Orders</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                  {totalOrders.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgba(245, 158, 11, 0.1)', 
              border: '1px solid rgba(245, 158, 11, 0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#f59e0b', mr: 2 }}>
                    <Assessment />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>Avg Order Value</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                  ${avgOrderValue.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgba(139, 92, 246, 0.1)', 
              border: '1px solid rgba(139, 92, 246, 0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#8b5cf6', mr: 2 }}>
                    <Inventory />
                  </Avatar>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>Inventory Items</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                  {totalItems}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ 
          bgcolor: 'rgba(255, 255, 255, 0.05)', 
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          mb: 3
        }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputBase-input': { color: '#ffffff' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiInputBase-input': { color: '#ffffff' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Tooltip title="Apply Filters">
                  <IconButton sx={{ color: '#3b82f6', border: '1px solid #3b82f6' }}>
                    <FilterList />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs Component */}
        <AnalyticsTabs value={value} handleTabChange={handleTabChange} data={data} />
      </Box>
    </div>
  );
};

export default CostAndInventoryUtilizationAnalytics;